import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { message, Modal } from "antd";
import { Input } from "../../src/ui/Input";
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import "./LoginPage.css";
import { AuthContext } from "../context/AuthContext";
import axios from "../axios/AxiosInstance";
import { restoreAgency } from "../services/agencyPreference";
import { getPostLoginPath } from "../services/authSession";

export default function LoginPage() {
  const { token } = useParams();
  const { setIsLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginPath = getPostLoginPath(location.search);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (token) {
      axios
        .post("auth/check/freelance/login", { token })
        .then(async (res) => {
          localStorage.setItem("isLogged", "true");
          localStorage.setItem("accessToken", res.data.access_token);
          localStorage.setItem("agencyName", res.data.agencyName);
          localStorage.setItem("agencyCity", res.data.agencyCity || "");
          localStorage.setItem("refreshToken", res.data.refresh_token);
          localStorage.setItem("userEmail", res.data.email);
          localStorage.setItem("userRole", res.data.role);
          localStorage.setItem("userId", res.data._id);
          localStorage.setItem("firstname", res.data.firstname);
          localStorage.setItem("lastname", res.data.lastname);
          if (!(await restoreAgency())) message.warning("Votre dernière agence n’a pas pu être restaurée. Vous pouvez la sélectionner dans le menu.");
          setIsLoggedIn(true);
          navigate(postLoginPath, { replace: true });
        })
        .catch((e) => {
          console.log("Token invalide");
          message.error("Token invalide");
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (event) => {
    event?.preventDefault();
    if (busy) return;
    setBusy(true); setAuthError("");
    try {
      const {data} = await axios.post("/auth/freelance/login", {username:email,password});
      if (!data?.requiresTwoFactor || !data.challengeId) throw new Error("La double authentification n’a pas pu démarrer.");
      setChallenge(data); setCode(""); setOpenModal(true);
    } catch (error) {
      const detail = error.response?.status === 401 ? "Identifiant ou mot de passe incorrect." : error.response?.data?.message || error.message || "Connexion impossible. Réessayez.";
      setAuthError(Array.isArray(detail) ? detail.join(" ") : detail);
    } finally { setBusy(false); }
  };
  const validateCode = async (event) => {
    event.preventDefault();
    if (busy || !challenge || !/^\d{6}$/.test(code)) return;
    setBusy(true); setAuthError("");
    try {
      const {data} = await axios.post("auth/freelance/validate-code",{challengeId:challenge.challengeId,code},{skipAuthFailureHandling:true});
      if (!data?.access_token || !data.refresh_token) throw new Error("Connexion non confirmée.");
      const values = {isLogged:"true",accessToken:data.access_token,refreshToken:data.refresh_token,agencyName:data.agencyName,agencyCity:data.agencyCity||"",userEmail:data.username,userRole:data.role,userId:data._id,firstname:data.firstname,lastname:data.lastname,phone:data.phone||""};
      Object.entries(values).forEach(([key,value])=>localStorage.setItem(key,value ?? ""));
      setPassword(""); setCode(""); setOpenModal(false);
      if (!(await restoreAgency())) message.warning("Votre dernière agence n’a pas pu être restaurée. Vous pouvez la sélectionner dans le menu.");
      setIsLoggedIn(true); navigate(postLoginPath,{replace:true});
    } catch (error) {setAuthError(error.response?.data?.message || "Code invalide ou expiré. Réessayez ou demandez un nouveau code.");}
    finally {setBusy(false);}
  };

  return (
    <main className="jm-login">
      {openModal && <Modal centered title="Vérification de connexion" open footer={null} onCancel={()=>{if(!busy){setOpenModal(false);setCode("");setAuthError("");}}} maskClosable={!busy} closable={!busy}>
        <form onSubmit={validateCode} className="space-y-4 pt-4">
          <p>Entrez le code à 6 chiffres envoyé à <strong>{challenge?.emailHint}</strong>. Il est valable 10 minutes.</p>
          <Input autoFocus aria-label="Code de connexion" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={event=>setCode(event.target.value.replace(/\D/g,""))} className="h-12 text-center text-xl tracking-widest" />
          {authError && <p role="alert" className="text-sm text-red-700">{authError}</p>}
          <button type="submit" disabled={busy || code.length !== 6} className="jm-login-submit">{busy ? "Vérification…" : "Valider le code"}</button>
          <button type="button" disabled={busy} onClick={login} className="w-full p-2 text-sm text-slate-600 underline disabled:opacity-50">Renvoyer un code</button>
        </form>
      </Modal>}
      <header className="jm-login-header">
        <a href="https://www.jm-interim.com/" className="jm-login-brand" aria-label="J’M Intérim, accueil"><img src="/brand/jm-interim-logo.jpg" alt="J’M Intérim — L’humain au cœur de nos missions" /></a>
        <a href="https://www.jm-interim.com/mon-espace" className="jm-login-back"><ArrowLeft size={16} aria-hidden="true" /> Tous les espaces</a>
      </header>
      <div className="jm-login-content">
      <section className="jm-login-access" aria-labelledby="login-title">
        <div className="jm-login-form-wrap">
          <div className="jm-login-card-heading"><span className="jm-login-symbol" aria-hidden="true"><LockKeyhole size={22} strokeWidth={1.6} /></span><p className="jm-login-kicker">ESPACE RECRUTEUR</p></div>
          <h1 id="login-title">Heureux de vous<br />retrouver<span>.</span></h1>
          <p className="jm-login-description">Connectez-vous à votre espace J’M Intérim.</p>
          <form onSubmit={login} className="jm-login-form" aria-label="Connexion à l’espace freelance">
            {!openModal && authError && <p role="alert" className="jm-login-error">{authError}</p>}
            <div>
              <label htmlFor="login-email">Identifiant</label>
              <div className="jm-login-input"><Mail size={19} aria-hidden="true" /><input id="login-email" name="username" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} required placeholder="Identifiant" value={email} onChange={event => setEmail(event.target.value)} /></div>
            </div>
            <div>
              <label htmlFor="login-password">Mot de passe</label>
              <div className="jm-login-input"><LockKeyhole size={19} aria-hidden="true" /><input id="login-password" name="password" type={isVisible ? "text" : "password"} autoComplete="current-password" required placeholder="Mot de passe" value={password} onChange={event => setPassword(event.target.value)} /><button type="button" aria-label={isVisible ? "Masquer le mot de passe" : "Afficher le mot de passe"} aria-pressed={isVisible} onClick={() => setIsVisible(current => !current)}>{isVisible ? <EyeOff size={19} /> : <Eye size={19} />}</button></div>
              <a href="/forgot-password" className="jm-login-forgot">Mot de passe oublié ?</a>
            </div>
            <button type="submit" disabled={busy} className="jm-login-submit"><span>{busy ? "Connexion en cours…" : "Connexion"}</span><ArrowRight size={20} aria-hidden="true" /></button>
          </form>
          <div className="jm-login-security"><ShieldCheck size={20} aria-hidden="true" /><p>Votre espace, en toute confiance.<span>Une vérification par code protège votre connexion.</span></p></div>
        </div>
      </section>
      </div>
      <footer className="jm-login-footer"><span>© {new Date().getFullYear()} J’M Intérim</span><span>L’humain au cœur de nos missions.</span><a href="https://www.jm-interim.com/contact">Contacter l’équipe</a></footer>
    </main>
  );
}
